import { useEffect, useState } from "react";
import { Sparkles, Headphones, BookmarkPlus, NotebookPen } from "lucide-react";
import { useSiteStatsSnapshot } from "@/hooks/useSiteStatsSnapshot";

const STORAGE_KEY = "atp:search-results-intro-seen:v2";

interface SearchResultsIntroModalProps {
  /** Only opens once this becomes true (i.e., results are on screen). */
  ready: boolean;
}

/**
 * One-time onboarding modal for the search results page.
 * Shows once per browser (localStorage gated), with no close button —
 * only the "Awesome, sounds great!" dismissal CTA.
 */
export default function SearchResultsIntroModal({ ready }: SearchResultsIntroModalProps) {
  const [open, setOpen] = useState(false);
  const { stats } = useSiteStatsSnapshot();
  const formatted = stats.episodes.toLocaleString("en-US");

  useEffect(() => {
    if (!ready) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Tiny delay so it doesn't slam in before the results paint
        const t = setTimeout(() => setOpen(true), 350);
        return () => clearTimeout(t);
      }
    } catch (_) {
      // localStorage unavailable (private mode etc.) — just show it this session
      setOpen(true);
    }
  }, [ready]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {}
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-intro-title"
      className="intro-modal-overlay"
    >
      <div className="intro-modal-card">
        {/* Comic-book corner badge */}
        <div className="intro-modal-badge">
          <Sparkles className="w-6 h-6" strokeWidth={3} />
        </div>

        <h2 id="search-intro-title" className="intro-modal-title">
          We found the perfect episodes for you! 🎧
        </h2>

        <p className="text-sm sm:text-base text-foreground leading-relaxed mb-4">
          We listened to{" "}
          <span className="font-bold text-accent">{formatted}</span> episodes
          and hand-picked the best{" "}
          <span className="font-bold">three</span> that answer your question.
        </p>

        <ul className="space-y-2.5 mb-5">
          <li className="flex items-start gap-3">
            <div className="flex-shrink-0 w-7 h-7 bg-[hsl(195,85%,55%)] border-[2px] border-foreground rounded-md flex items-center justify-center text-white shadow-[2px_2px_0px_0px_hsl(var(--foreground))] transform rotate-[-3deg]">
              <Headphones className="w-3.5 h-3.5" strokeWidth={3} />
            </div>
            <p className="text-xs sm:text-sm text-foreground leading-snug pt-1">
              <span className="font-bold">Press play</span> — each episode is cued to the most relevant moment.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="flex-shrink-0 w-7 h-7 bg-primary border-[2px] border-foreground rounded-md flex items-center justify-center text-primary-foreground shadow-[2px_2px_0px_0px_hsl(var(--foreground))] transform rotate-[2deg]">
              <BookmarkPlus className="w-3.5 h-3.5" strokeWidth={3} />
            </div>
            <p className="text-xs sm:text-sm text-foreground leading-snug pt-1">
              <span className="font-bold">Save</span> episodes to your list for later.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="flex-shrink-0 w-7 h-7 bg-accent border-[2px] border-foreground rounded-md flex items-center justify-center text-accent-foreground shadow-[2px_2px_0px_0px_hsl(var(--foreground))] transform rotate-[-2deg]">
              <NotebookPen className="w-3.5 h-3.5" strokeWidth={3} />
            </div>
            <p className="text-xs sm:text-sm text-foreground leading-snug pt-1">
              <span className="font-bold">Take notes</span> on what stood out so you can reference them anytime.
            </p>
          </li>
        </ul>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={dismiss}
            autoFocus
            className="btn-comic-primary btn-comic-lg"
          >
            Awesome, sounds great! ✨
          </button>
        </div>
      </div>
    </div>
  );
}
